import { useUser } from '@/app/providers';
import { FloatingDeleteButton } from '@/components';
import { PageContentCard } from '@/components/layout/PageContentCard';
import PageWithHeadline from '@/components/layout/PageWithHeadline';
import { useSearch } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CompanyDevicesSection, DeviceSidePanel } from './components';
import { useDeleteDevices } from './hooks';
import type { DeviceListState } from './hooks';
import { logger } from '@/lib';

interface DevicesSearchParams {
  page?: number;
  pageSize?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// Keeps the list state in the URL without pushing a history entry, so a reload restores the view.
function setSearchParams(params: DevicesSearchParams) {
  const url = new URL(window.location.href);

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, String(value));
    } else {
      url.searchParams.delete(key);
    }
  });
  window.history.replaceState({}, '', url.toString());
}

export function DeviceManagementPage() {
  const { t } = useTranslation();
  const { user } = useUser();
  const companyId = user?.companyUuid || '';

  const [selectedCompany, setSelectedCompany] = useState<{ id: string; name: string } | null>(null);
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);

  // Read once as the seed for the list; the list reports its changes back into the URL.
  const searchParams = useSearch({ from: '/_app/devices/' });
  const initialState = useMemo(
    () => ({ page: searchParams.page, pageSize: searchParams.pageSize, search: searchParams.search }),
    [searchParams.page, searchParams.pageSize, searchParams.search]
  );
  const sort = useMemo(
    () => ({ sortBy: searchParams.sortBy, sortOrder: searchParams.sortOrder }),
    [searchParams.sortBy, searchParams.sortOrder]
  );

  const handleStateChange = useCallback((state: DeviceListState) => {
    setSearchParams({ page: state.page, pageSize: state.pageSize, search: state.search || undefined });
  }, []);

  const deleteDevices = useDeleteDevices();

  const handleCompanyClick = useCallback((company: { id: string; name: string }) => {
    setSelectedCompany(company);
    setIsSidePanelOpen(true);
  }, []);

  const handleCloseSidePanel = useCallback(() => {
    setIsSidePanelOpen(false);
    setSelectedCompany(null);
  }, []);

  return (
    <PageWithHeadline headlineKey="devices.title">
      <PageContentCard className="flex flex-col grow w-full overflow-hidden">
        <CompanyDevicesSection
          companyId={companyId}
          initialState={initialState}
          onStateChange={handleStateChange}
          sort={sort}
          onCompanyClick={handleCompanyClick}
          renderSelectionActions={({ selectedIds, clearSelection }) => (
            <FloatingDeleteButton
              onDelete={async () => {
                try {
                  await deleteDevices.mutateAsync(Array.from(selectedIds));
                  clearSelection();
                } catch (error) {
                  logger.error('Failed to delete devices', { error });
                }
              }}
              isDeleting={deleteDevices.isPending}
              modalTitle={t('devices.deleteModal.title')}
              modalMessage={t('devices.deleteModal.message')}
            />
          )}
        />
      </PageContentCard>

      {/* The list navigates to the device detail page, so the panel only ever shows a company. */}
      <DeviceSidePanel
        device={null}
        company={selectedCompany}
        view="company"
        isOpen={isSidePanelOpen}
        onClose={handleCloseSidePanel}
      />
    </PageWithHeadline>
  );
}
